import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { Skull, Smile } from "lucide-react";

import { useAuth } from "../../context/AuthContext";

import api from "../../api/axios";

const Login = () => {
    const navigate = useNavigate();

    const { setEmployee } = useAuth();

    const [email, setEmail] = useState("");

    const [password, setPassword] =
        useState("");

    const [showPassword, setShowPassword] =
        useState(false);

    const [error, setError] = useState("");

    const [success, setSuccess] =
        useState("");

    const [loading, setLoading] =
        useState(false);

    const [showForceLogin, setShowForceLogin] =
        useState(false);

    const [
        showForgotPassword,
        setShowForgotPassword,
    ] = useState(false);

    // ================= LOGIN =================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) return;

        setError("");

        setSuccess("");

        setLoading(true);

        try {
            const loginRes = await api.post(
                "/api/employee/auth/login",
                {
                    email,
                    password,
                },
            );

            if (loginRes.data?.token) {
                localStorage.setItem("employee_token", loginRes.data.token);
            }

            if (loginRes.data?.employee) {
                setEmployee(loginRes.data.employee);
            }

            navigate("/dashboard", {
                replace: true,
            });
        } catch (err) {
            if (err.response) {
                if (
                    err.response.status === 409
                ) {
                    setShowForceLogin(true);

                    setError("");
                } else if (
                    err.response.status === 401
                ) {
                    setError(
                        "Invalid email or password",
                    );
                } else if (
                    err.response.status === 403
                ) {
                    setError(
                        "Account disabled",
                    );
                } else {
                    setError(
                        err.response?.data
                            ?.message ||
                            "Server error. Try again.",
                    );
                }
            } else {
                setError(
                    "Network error. Check your connection.",
                );
            }
        } finally {
            setLoading(false);
        }
    };

    // ================= FORCE LOGIN =================
    const handleForceLogin = async () => {
        setLoading(true);

        setError("");

        try {
            const loginRes = await api.post(
                "/api/employee/auth/force-login",
                {
                    email,
                    password,
                },
            );

            if (loginRes.data?.token) {
                localStorage.setItem("employee_token", loginRes.data.token);
            }

            if (loginRes.data?.employee) {
                setEmployee(loginRes.data.employee);
            }

            navigate("/dashboard", {
                replace: true,
            });
        } catch (err) {
            setError(
                err.response?.data
                    ?.message ||
                    "Server error. Try again.",
            );

            setShowForceLogin(false);
        } finally {
            setLoading(false);
        }
    };

    // ================= FORGOT PASSWORD =================
    const handleForgotPassword =
        async () => {
            if (!email) {
                return setError(
                    "Please enter your email address",
                );
            }

            try {
                setLoading(true);

                setError("");

                setSuccess("");

                const res = await api.post(
                    "/api/employee/auth/forgot-password",
                    {
                        email,
                    },
                );

                setSuccess(
                    res.data.message,
                );
            } catch (err) {
                setError(
                    err.response?.data
                        ?.message ||
                    "Failed to send reset email",
                );
            } finally {
                setLoading(false);
            }
        };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4 transition-colors duration-300">

            <div className="w-full max-w-sm bg-[var(--card-bg)] p-8 rounded-3xl shadow-[var(--shadow-soft)] border border-[var(--border-color)]">

                {/* LOGO */}
                <div className="flex justify-center mb-6">
                    <img 
                        src="/logo_light.png?v=3" 
                        alt="Savrion Technologies" 
                        className="h-10 w-auto object-contain dark:hidden" 
                    />
                    <img 
                        src="/logo_dark.png?v=3" 
                        alt="Savrion Technologies" 
                        className="h-10 w-auto object-contain hidden dark:block" 
                    />
                </div>

                {/* TITLE */}
                <h2 className="text-2xl font-bold text-[var(--text-primary)] text-center mb-1">
                    Employee Login
                </h2>

                <p className="text-gray-500 text-sm text-center mb-6">
                    Savrion Technologies Team Portal
                </p>

                {/* ERROR */}
                {error && (
                    <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3">
                        {error}
                    </div>
                )}

                {/* SUCCESS */}
                {success && (
                    <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm rounded-xl px-4 py-3">
                        {success}
                    </div>
                )}

                {!showForceLogin ? (
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-5"
                    >

                        {/* EMAIL */}
                        <div>

                            <label className="block text-sm text-gray-400 mb-2">
                                Email
                            </label>

                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={email}
                                onChange={(e) =>
                                    setEmail(
                                        e.target.value,
                                    )
                                }
                                required
                                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] outline-none focus:border-emerald-500 transition"
                            />

                        </div>

                        {/* PASSWORD */}
                        {!showForgotPassword && (
                            <div>

                                <label className="block text-sm text-gray-400 mb-2">
                                    Password
                                </label>

                                <div className="relative">

                                    <input
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        placeholder="Enter your password"
                                        value={
                                            password
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setPassword(
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                        required
                                        className="w-full px-4 py-3 pr-14 rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] outline-none focus:border-emerald-500 transition"
                                    />

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setShowPassword(
                                                !showPassword,
                                            )
                                        }
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-emerald-400 transition"
                                    >
                                        {showPassword ? (
                                            <Smile
                                                size={
                                                    20
                                                }
                                            />
                                        ) : (
                                            <Skull
                                                size={
                                                    20
                                                }
                                            />
                                        )}
                                    </button>

                                </div>

                            </div>
                        )}

                        {/* LOGIN BUTTON */}
                        {!showForgotPassword && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-emerald-500 hover:bg-emerald-600 transition text-black py-3 rounded-xl font-semibold disabled:opacity-60"
                            >
                                {loading
                                    ? "Logging in..."
                                    : "Login"}
                            </button>
                        )}

                        {/* FORGOT PASSWORD */}
                        {showForgotPassword ? (
                            <button
                                type="button"
                                onClick={
                                    handleForgotPassword
                                }
                                disabled={loading}
                                className="bg-emerald-500 hover:bg-emerald-600 transition text-black py-3 rounded-xl font-semibold disabled:opacity-60"
                            >
                                {loading
                                    ? "Sending..."
                                    : "Send Reset Link"}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForgotPassword(
                                        true,
                                    );

                                    setError("");

                                    setSuccess("");
                                }}
                                className="text-sm text-emerald-400 hover:text-emerald-300 transition text-center"
                            >
                                Forgot Password?
                            </button>
                        )}

                        {/* BACK */}
                        {showForgotPassword && (
                            <button
                                type="button"
                                onClick={() => {
                                    setShowForgotPassword(
                                        false,
                                    );

                                    setError("");

                                    setSuccess("");
                                }}
                                className="text-sm text-gray-400 hover:text-white transition text-center"
                            >
                                Back to Login
                            </button>
                        )}

                    </form>
                ) : (
                    <div className="flex flex-col gap-5 text-center">

                        <p className="text-gray-300 text-sm leading-6">
                            You are already logged in on another device.
                            <br />
                            Do you want to logout that device and login here?
                        </p>

                        <button
                            onClick={
                                handleForceLogin
                            }
                            disabled={loading}
                            className="bg-emerald-500 hover:bg-emerald-600 transition text-black py-3 rounded-xl font-semibold disabled:opacity-60"
                        >
                            {loading
                                ? "Processing..."
                                : "Yes, Login Here"}
                        </button>

                        <button
                            onClick={() =>
                                setShowForceLogin(
                                    false,
                                )
                            }
                            className="bg-[#2a2a2a] hover:bg-[#3a3a3a] transition text-white py-3 rounded-xl"
                        >
                            Cancel
                        </button>

                    </div>
                )}

            </div>
        </div>
    );
};

export default Login;
